import { useState } from "react";
import { formatShopifyMoney } from "../../helpers/helpers.tsx";
import type { BundleDisplay } from "../../services/GiftSetService";
import localStyles from "./styles.module.scss";

// Collapsible "$X in FREE gifts included with your order" strip shown right
// after the Add To Cart button when a gift-set promo is active — modeled on
// the .scs-lsc-gift / .scs-lsc-gift__panel markup from the Intelligems
// "New PDP Design Test Less Cognitive Load - Simplified" (Variation 1)
// experiment on /pages/love-your-ride-deal. Replaces SuperOffer's
// .bf_wrapper banner for these promos (SuperOffer hides itself when a
// bundleDisplay override is active).
interface GiftBundleStripProps {
    bundleDisplay: BundleDisplay;
}

const parsePrice = (price?: string): number => parseInt((price ?? '0').replace(/[^0-9.]/g, '')) || 0;

export default function GiftBundleStrip({ bundleDisplay }: GiftBundleStripProps) {
    const [expanded, setExpanded] = useState(false);
    const items = (bundleDisplay.items || []).filter((item) => item.title?.trim());
    if (items.length === 0) return null;

    const totalValue = items.reduce((sum, item) => sum + parsePrice(item.price), 0);

    return (
        <div className={`${localStyles.gift}`}>
            <button
                type="button"
                className={`${localStyles.toggle}`}
                aria-expanded={expanded}
                aria-controls="scs-gift-bundle-panel"
                onClick={() => setExpanded((v) => !v)}
            >
                <div className={`${localStyles.thumbs}`}>
                    {items.slice(0, 4).map((item, i) => (
                        <span key={i} className={`${localStyles.thumb}`}>
                            {item.imageUrl ? <img src={item.imageUrl} alt={item.alt || ''} /> : null}
                        </span>
                    ))}
                </div>
                <p className={`${localStyles.text}`}>
                    <span className={`${localStyles.line}`}>
                        {totalValue > 0 ? <span className={`${localStyles.amount}`}>{formatShopifyMoney(totalValue)}</span> : null}
                        {' '}<strong>in FREE gifts</strong>
                    </span>
                    {' '}included with your order
                </p>
                <svg
                    className={`${localStyles.chevron} ${expanded ? localStyles.chevronOpen : ''}`}
                    width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M1 1L7 7L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {expanded ? (
                <div id="scs-gift-bundle-panel" className={`${localStyles.panel}`}>
                    <ul className={`${localStyles.list}`}>
                        {items.map((item, i) => (
                            <li key={i} className={`${localStyles.item}`}>
                                <span className={`${localStyles.thumb}`}>
                                    {item.imageUrl ? <img src={item.imageUrl} alt={item.alt || ''} /> : null}
                                </span>
                                <span className={`${localStyles.itemCopy}`}>
                                    <span className={`${localStyles.itemTitle}`}>
                                        {item.title}
                                        {item.price ? <s className={`${localStyles.itemPrice}`}>{formatShopifyMoney(parsePrice(item.price))}</s> : null}
                                    </span>
                                    {item.desc ? <span className={`${localStyles.itemDesc}`}>{item.desc}</span> : null}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}
